import {
  V2ElectricMapDataGgdDataPostResponse,
  V2LightmapSunlightDevicePagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import switchOnPng from '../../imgs/switch-on.png';
import switchOffPng from '../../imgs/switch-off.png';
import markerPng from '../../imgs/marker.png';
import areaMarkerPng from '../../imgs/area-marker.png';
import { AreaItem, SunLightItem } from '.';
import { keepTwoOrLine, keepZeroOrLine } from '@/utils/utils';
import { RTU_STATUS, RTU_STATUS_MAP } from '../SunLightPanel/util';
import { isNil } from 'lodash-es';

// 类型(1、高杆灯 2、太阳能路灯 3、车间照明)
export enum LightTypeEnum {
  GGD = 1,
  SUN_LIGHT = 2,
  WORKSPACE_LIGHT = 3,
}

export const LightTypeEnumDisplay = {
  [LightTypeEnum.GGD]: '高杆灯',
  [LightTypeEnum.SUN_LIGHT]: '太阳能路灯',
  [LightTypeEnum.WORKSPACE_LIGHT]: '车间照明',
};

export function createPulsingDot(size: number, color: string, map: any) {
  let lastRender = 0; // 上一帧时间戳

  return {
    width: size,
    height: size,
    data: new Uint8ClampedArray(size * size * 4),
    canvas: undefined as HTMLCanvasElement | undefined,
    context: undefined as CanvasRenderingContext2D | null | undefined,

    onAdd() {
      this.canvas = document.createElement('canvas');
      this.canvas.width = this.width;
      this.canvas.height = this.height;
      this.context = this.canvas.getContext('2d', { willReadFrequently: true });
    },

    onRemove() {
      this.context?.clearRect(0, 0, this.width, this.height);
      this.canvas = undefined;
    },

    render() {
      const ctx = this.context;
      if (!ctx) return false;

      const now = performance.now();
      // ✅ 优化点2：控制帧率（约30FPS）
      if (now - lastRender < 33) return true;
      lastRender = now;

      const t = (performance.now() % 1000) / 1000;
      const radius = this.width / 4;
      const outerRadius = radius + (this.width / 2 - radius) * t;

      ctx.clearRect(0, 0, this.width, this.height);
      ctx.beginPath();
      ctx.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${color},${1 - t})`;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${color},1)`;
      ctx.fill();

      this.data = ctx.getImageData(0, 0, this.width, this.height).data as any;
      map.triggerRepaint();
      return true;
    },
  };
}

export function createLightPopupHtml(data: V2ElectricMapDataGgdDataPostResponse) {
  return `
   <div style="
  width:320px;
  height:208px;
  background:linear-gradient(180deg,#041b33 0%,#001c37 100%);
  box-shadow:0px 12px 48px 16px rgba(0,0,0,0.12),
             0px 9px 28px 0px rgba(0,0,0,0.2),
             0px 6px 16px -8px rgba(0,0,0,0.32);
  border-radius:2px;
  border:1px solid rgba(74,144,226,0.5);
  padding:12px 16px;
  position:relative;
  z-index:20;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial;
">
  <div style="display:flex;justify-content:space-between;align-items:center;">
    <div style="font-weight:500;font-size:16px;color:rgba(255,255,255,0.85);">
      ${data?.name ?? '--'}
    </div>
    <button
      id="closePopupBtn"
      type="button"
      style="
        background-color: transparent;
        cursor: pointer;
        border: none;
        display: flex;
        justify-content: center;
        align-items: center;
      "
    >
      <div style="cursor:pointer;color:rgba(255,255,255,0.6);font-size:16px;">✕</div>
    </button>
  </div>

  <div style="display:flex;column-gap:34px;align-items:center;margin-top:16px;">
    <div style="display:flex;align-items:center;">
      <div style="font-weight:400;font-size:14px;color:rgba(255,255,255,0.85);margin-top:0;">状态：</div>
      ${
        data.intStatus === 2
          ? '<span style="font-size:14px;color:rgba(255,255,255,0.6);">离线</span>'
          : `<img id="status-switch" style="width: 44px;height: 22px;cursor:pointer;" src="${
              data.intStatus === 1 ? switchOnPng : switchOffPng
            }" />`
      }
    </div>

    <div style="font-weight:400;font-size:14px;color:rgba(255,255,255,0.85);margin-top:0;">
      功率：
      <span style="
        font-family:YouSheBiaoTiHei;
        font-size:18px;
        background:linear-gradient(180deg,#00aeff 0%,#65ffe9 39%,#00d4ff 100%);
        -webkit-background-clip:text;
        -webkit-text-fill-color:transparent;
      ">${keepTwoOrLine(data.power)}kW</span>
    </div>
  </div>

  <div style="font-weight:400;font-size:14px;color:rgba(255,255,255,0.85);margin-top:16px;">
    温度：<span style="font-weight:500;font-size:14px;color:#ffffff;">${data.temperature ?? '--'}℃</span>
  </div>

  <div style="font-weight:400;font-size:14px;color:rgba(255,255,255,0.85);margin-top:16px;">
    A/B/C三相电压：
    <span style="color:#FFC53D;">${keepZeroOrLine(data.avoltage)}</span>
    <span style="color:#FFF;">/</span>
    <span style="color:#52C41A;">${keepZeroOrLine(data.bvoltage)}</span>
    <span style="color:#FFF;">/</span>
    <span style="color:#E64242;">${keepZeroOrLine(data.cvoltage)}</span>
    <span style="color:#FFF;margin-left:4px;">V</span>
  </div>

  <div style="font-weight:400;font-size:14px;color:rgba(255,255,255,0.85);margin-top:16px;">
    A/B/C三相电流：
    <span style="color:#FFC53D;">${keepZeroOrLine(data.acurrent)}</span>
    <span style="color:#FFF;">/</span>
    <span style="color:#52C41A;">${keepZeroOrLine(data.bcurrent)}</span>
    <span style="color:#FFF;">/</span>
    <span style="color:#E64242;">${keepZeroOrLine(data.ccurrent)}</span>
    <span style="color:#FFF;margin-left:4px;">A</span>
  </div>
</div>
  `;
}
export function createSunLightPopupHtml(data?: SunLightItem) {
  const itemSty = 'font-weight:400;font-size:14px;color:rgba(255,255,255,0.85);margin-top:8px;width:calc(50% - 8px);';
  const rtuColor = (rtuStatus?: number) => {
    if (isNil(rtuStatus)) return 'rgba(255,255,255,0.85)';
    return [RTU_STATUS.idle].includes(rtuStatus)
      ? 'rgba(255,255,255,0.85)'
      : [
          RTU_STATUS.charging_1,
          RTU_STATUS.charging_2,
          RTU_STATUS.charging_3,
          RTU_STATUS.charging_4,
          RTU_STATUS.charging_5,
        ].includes(rtuStatus)
      ? '#52C41A'
      : [RTU_STATUS.discharge_1, RTU_STATUS.discharge_2, RTU_STATUS.discharge_end].includes(rtuStatus)
      ? '#4A90E2'
      : [RTU_STATUS.manual_light].includes(rtuStatus)
      ? '#4A90E2'
      : '#E64242';
  };
  const rtuBorder = (rtuStatus?: number) => {
    if (isNil(rtuStatus)) return '1px solid rgba(255,255,255,0.2)';
    return [RTU_STATUS.idle].includes(rtuStatus)
      ? '1px solid rgba(255,255,255,0.2)'
      : [
          RTU_STATUS.charging_1,
          RTU_STATUS.charging_2,
          RTU_STATUS.charging_3,
          RTU_STATUS.charging_4,
          RTU_STATUS.charging_5,
        ].includes(rtuStatus)
      ? '1px solid rgba(82, 196, 26, 0.5)'
      : [RTU_STATUS.discharge_1, RTU_STATUS.discharge_2, RTU_STATUS.discharge_end].includes(rtuStatus)
      ? '1px solid rgba(74, 144, 226, 0.5)'
      : [RTU_STATUS.manual_light].includes(rtuStatus)
      ? '1px solid rgba(74, 144, 226, 0.5)'
      : '1px solid rgba(230, 66, 66, 0.5)';
  };
  const rtuBg = (rtuStatus?: number) => {
    if (isNil(rtuStatus)) return 'rgba(255,255,255,0.05)';
    return [RTU_STATUS.idle].includes(rtuStatus)
      ? 'rgba(255,255,255,0.05)'
      : [
          RTU_STATUS.charging_1,
          RTU_STATUS.charging_2,
          RTU_STATUS.charging_3,
          RTU_STATUS.charging_4,
          RTU_STATUS.charging_5,
        ].includes(rtuStatus)
      ? 'rgba(82, 196, 26, 0.1)'
      : [RTU_STATUS.discharge_1, RTU_STATUS.discharge_2, RTU_STATUS.discharge_end].includes(rtuStatus)
      ? 'rgba(74, 144, 226, 0.1)'
      : [RTU_STATUS.manual_light].includes(rtuStatus)
      ? 'rgba(74, 144, 226, 0.1)'
      : 'rgba(230, 66, 66, 0.1)';
  };

  return `
   <div style="
  width:320px;
  height:254px;
  background:linear-gradient(180deg,#041b33 0%,#001c37 100%);
  box-shadow:0px 12px 48px 16px rgba(0,0,0,0.12),
             0px 9px 28px 0px rgba(0,0,0,0.2),
             0px 6px 16px -8px rgba(0,0,0,0.32);
  border-radius:2px;
  border:1px solid rgba(74,144,226,0.5);
  padding:12px 16px;
  position:relative;
  z-index:20;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial;
">
  <div style="display:flex;justify-content:space-between;align-items:center;">
    <div style="font-weight:500;font-size:16px;color:rgba(255,255,255,0.85);">
      ${data?.deviceName ?? '--'}
    </div>

    <div style="display:flex;align-items:center;">
      <div id="more" style="cursor:pointer;color:#4A90E2;font-size:16px;margin-right:16px">更多 >></div>
      <button
        id="closePopupBtn"
        type="button"
        style="
          background-color: transparent;
          cursor: pointer;
          border: none;
          display: flex;
          justify-content: center;
          align-items: center;
        "
      >
        <div style="cursor:pointer;color:rgba(255,255,255,0.6);font-size:16px;">✕</div>
      </button>
    </div>
  </div>

  <div style="display:flex;column-gap:8px;flex-wrap: wrap;margin-top:16px;">

  <div style="${itemSty}">
    状态：<span style="
        font-size: 12px;
        line-height: 20px;
        list-style: none;
        display: inline-block;
        height: auto;
        margin-inline-end: 8px;
        padding-inline: 7px;
        white-space: nowrap;
        border-radius: 2px;
        opacity: 1;
        text-align: start;
        position: relative;
        font-size:16px;
        color:${(() => {
          const statusStyles: Record<number, { color: string; border: string; bg: string; text: string }> = {
            1: { color: '#52C41A', border: '#52C41A50', bg: 'rgba(82,196,26,0.1)', text: '开启' },
            0: {
              color: 'rgba(255,255,255,0.65)',
              border: 'rgba(255,255,255,0.2)',
              bg: 'rgba(255,255,255,0.05)',
              text: '关闭',
            },
          };
          const defaultStyle = {
            color: '#FAAD14',
            border: 'rgba(250,173,20,0.5)',
            bg: 'rgba(250,173,20,0.1)',
            text: '离线',
          };
          const unknownStyle = {
            color: 'rgba(255,255,255,0.85)',
            border: 'rgba(255,255,255,0.2)',
            bg: 'rgba(255,255,255,0.05)',
            text: '-',
          };
          return !isNil(data?.intStatus) ? (statusStyles[data!.intStatus] ?? defaultStyle).color : unknownStyle.color;
        })()};
        border:${(() => {
          const statusStyles: Record<number, string> = { 1: '#52C41A50', 0: 'rgba(255,255,255,0.2)' };
          return !isNil(data?.intStatus)
            ? statusStyles[data!.intStatus] ?? 'rgba(250,173,20,0.5)'
            : 'rgba(255,255,255,0.2)';
        })()};
        background-color:${(() => {
          const statusStyles: Record<number, string> = { 1: 'rgba(82,196,26,0.1)', 0: 'rgba(255,255,255,0.05)' };
          return !isNil(data?.intStatus)
            ? statusStyles[data!.intStatus] ?? 'rgba(250,173,20,0.1)'
            : 'rgba(255,255,255,0.05)';
        })()};
      ">${(() => {
        const statusText: Record<number, string> = { 1: '开启', 0: '关闭' };
        return !isNil(data?.intStatus) ? statusText[data!.intStatus] ?? '离线' : '-';
      })()}</span>
  </div>
  <div style="${itemSty}">
    温度：<span style="font-weight:500;font-size:14px;color:#ffffff;">${data?.temperature ?? '--'}℃</span>
  </div>
  <div style="${itemSty}">
    电池电量：<span style="font-weight:500;font-size:14px;color:#ffffff;">${data?.batteryCapacity ?? '--'}kWh</span>
  </div>
  <div style="${itemSty}">
    太阳板电压：<span style="font-weight:500;font-size:14px;color:#ffffff;">${data?.solarPanelVoltage ?? '--'}V</span>
  </div>
  <div style="${itemSty}">
    蓄电池电压：<span style="font-weight:500;font-size:14px;color:#ffffff;">${data?.electricityVoltage ?? '--'}V</span>
  </div>
  <div style="font-weight:400;font-size:14px;color:rgba(255,255,255,0.85);margin-top:6px;width:100%;">
    放电电压：<span style="font-weight:500;font-size:14px;color:#52E7FF;">${data?.dischargeVoltage ?? '--'}V</span>
  </div>
  <div style="${itemSty}">
    充电电流：<span style="font-weight:500;font-size:14px;color:#16DD8E;">${data?.chargingCurrent ?? '--'}A</span>
  </div>
  <div style="${itemSty}">
    放电电流：<span style="font-weight:500;font-size:14px;color:#52E7FF;">${data?.dischargeCurrent ?? '--'}A</span>
  </div>
  <div style="${itemSty}">
    总充电安时数：<span style="font-weight:500;font-size:14px;color:#16DD8E;">${
      data?.totalChargingAmpereHours ?? '--'
    }h</span>
  </div>
  <div style="${itemSty}">
    总放电安时数：<span style="font-weight:500;font-size:14px;color:#52E7FF;">${
      data?.totalDischargingAmpereHours ?? '--'
    }h</span>
  </div>

    
  </div>
</div>
  `;
}

export function createDom(area: AreaItem): string {
  // const isSunLightMarker = area.existSunlight;

  // const bgColor = isSunLightMarker ? 'rgba(4,83,52,0.8)' : 'rgba(44,26,133,0.8)';
  // const showText = isSunLightMarker ? '路灯' : '车间照明';
  // const width = isSunLightMarker ? 66 : 216;
  // const backgroundImage = isSunLightMarker ? markerPng : areaMarkerPng;

  // // 数量
  // const count = isSunLightMarker ? area.lightData?.count : area.circuitData?.count;
  // const closeCount = isSunLightMarker ? area.lightData?.closeCount : area.circuitData?.closeCount;
  // const openCount = isSunLightMarker ? area.lightData?.openCount : area.circuitData?.openCount;

  const bgColor = 'rgba(44,26,133,0.8)';
  const showText = '车间照明';
  const width = 280;
  const backgroundImage = areaMarkerPng;

  // 数量
  const count = area.circuitData?.count;
  const closeCount = area.circuitData?.closeCount;
  const openCount = area.circuitData?.openCount;
  const offlineCount = area.circuitData?.offlineCount;

  return `
    <div style="display:flex;flex-direction:column;justify-content:center;align-items:center;cursor:pointer;">
      <div id="marker-outer-${area.id}" style="
        display:none;
        position:relative;
        border-radius:2px;
        background-color:${bgColor};
        padding:4px 8px;
        box-sizing:border-box;
        border:1px solid #ffffff60;
        flex-direction:row;
        justify-content:space-between;
        align-items:center;
        width:${width}px;
        z-index:999;
      ">
        <div style="flex:1;text-align:center">
          <div style="color:#52E7FF;font-size:14px;font-weight:500;">${showText}</div>
          <div style="color:#FFF;font-size:14px;font-weight:500;">
            ${count}
          </div>
        </div>
        <div style="height:31px;width:1px;background-color:rgba(255,255,255,0.2);margin:0 4px;"></div>
        <div style="flex:1;text-align:center">
          <div style="color:#52E7FF;font-size:14px;font-weight:500;">开启</div>
          <div style="color:#16DD8E;font-size:14px;font-weight:500;">
            ${closeCount}
          </div>
        </div>
        <div style="height:31px;width:1px;background-color:rgba(255,255,255,0.2);margin:0 4px;"></div>
        <div style="flex:1;text-align:center">
        <div style="color:#52E7FF;font-size:14px;font-weight:500;">关闭</div>
          <div style="color:#B3B3B3;font-size:14px;font-weight:500;">
           ${openCount}
          </div>
        </div>
        <div style="height:31px;width:1px;background-color:rgba(255,255,255,0.2);margin:0 4px;"></div>
        <div style="flex:1;text-align:center">
          <div style="color:#52E7FF;font-size:14px;font-weight:500;">离线</div>
          <div style="color:#FF9247;font-size:14px;font-weight:500;">
            ${offlineCount ?? 0}
          </div>
        </div>

      </div>
      <div id="marker-icon-${area.id}" style="
        width:25px;
        height:28px;
        background-image:url(${backgroundImage});
        background-repeat:no-repeat;
        background-size:100% 100%;
        background-position:center 0;
        margin:0;
        z-index:-999;
      "></div>
    </div>
  `;
}
